"use client";

// Fondo canvas del Hero — modo "grid" (`heroBg: "grid"` en TWEAK_DEFAULTS,
// confirmado contra las capturas reales: grid de líneas finas + 3 "beams"
// violeta que recorren líneas al azar en loop). Traducción 1:1 de
// `drawGrid()`/`newBeam()`/`loop()` del handoff — requestAnimationFrame,
// no setInterval. Respeta prefers-reduced-motion (dibuja un frame estático).

import { useEffect, useRef } from "react";

const GAP = 56;
const INK = { r: 21, g: 16, b: 61 };

type Beam = { horiz: boolean; line: number; pos: number; sp: number };

function newBeam(W: number, H: number, delay = 0): Beam {
  const horiz = Math.random() < 0.5;
  const lines = Math.floor((horiz ? H : W) / GAP);
  return {
    horiz,
    line: (1 + Math.floor(Math.random() * Math.max(1, lines - 1))) * GAP,
    pos: -200 - delay * 300,
    sp: 2.2 + Math.random() * 1.8,
  };
}

export function HeroCanvasBg() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let W = 0, H = 0, dpr = 1;
    let beams: Beam[] = [];
    let raf: number | null = null;

    function setup() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = wrap!.getBoundingClientRect();
      W = r.width; H = r.height;
      canvas!.width = W * dpr; canvas!.height = H * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    function initBeams() {
      beams = [0, 1, 2].map((i) => newBeam(W, H, i * 1.4));
    }
    function drawGrid() {
      ctx!.clearRect(0, 0, W, H);
      ctx!.strokeStyle = `rgba(${INK.r},${INK.g},${INK.b},.055)`;
      ctx!.lineWidth = 1;
      for (let x = GAP; x < W; x += GAP) { ctx!.beginPath(); ctx!.moveTo(x, 0); ctx!.lineTo(x, H); ctx!.stroke(); }
      for (let y = GAP; y < H; y += GAP) { ctx!.beginPath(); ctx!.moveTo(0, y); ctx!.lineTo(W, y); ctx!.stroke(); }
      for (let i = 0; i < beams.length; i++) {
        const bm = beams[i];
        bm.pos += bm.sp;
        const len = 190;
        let g: CanvasGradient;
        if (bm.horiz) g = ctx!.createLinearGradient(bm.pos, 0, bm.pos + len, 0);
        else g = ctx!.createLinearGradient(0, bm.pos, 0, bm.pos + len);
        g.addColorStop(0, "rgba(107,92,255,0)");
        g.addColorStop(0.5, "rgba(107,92,255,.55)");
        g.addColorStop(1, "rgba(107,92,255,0)");
        ctx!.strokeStyle = g; ctx!.lineWidth = 1.6;
        ctx!.beginPath();
        if (bm.horiz) { ctx!.moveTo(bm.pos, bm.line); ctx!.lineTo(bm.pos + len, bm.line); }
        else { ctx!.moveTo(bm.line, bm.pos); ctx!.lineTo(bm.line, bm.pos + len); }
        ctx!.stroke();
        if (bm.pos > (bm.horiz ? W : H) + 100) beams[i] = newBeam(W, H);
      }
    }
    function loop() { drawGrid(); raf = requestAnimationFrame(loop); }

    setup();
    initBeams();
    if (reduced) { drawGrid(); return; }
    raf = requestAnimationFrame(loop);

    function onResize() { setup(); initBeams(); }
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={wrapRef} className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
    </div>
  );
}
